import * as React from "react";
import { ChangeEventHandler, useEffect } from "react";
import { useRecoilValue } from "recoil";
import { getCertificateCreateFormAtom } from "raft/CertificateForm";
import {
  isCertificateToReplaceOperationAtom,
  isUploadValidExtendedByAtom,
} from "pages/UploadCertificate/state/uploadCertificateStateBase";
import Props from "./index.d";
import ordinaryColors from "themes/ordinaryColors";
import { TextareaAutosize } from "@mui/material";
import styled from "@mui/system/styled";
import translations from "translations";
import { hasNonLatinCharacters } from "utils/hasNonLatinCharacters";

// Status ID constants for instructions field clearing
const STATUS_IDS = {
  WITHDRAWN_NOT_REPLACED: "64e89d04-9533-57f4-8f2d-41f340a29d8b",
  VALID_EXTENDED_BY: "02ab294a-cfe3-57d0-8831-2d600c44397c",
};

export const StyledTextArea = styled(TextareaAutosize, {
  label: "TextStyledTextArea",
  shouldForwardProp(propName) {
    return propName !== "hasError" && propName !== "isUnit";
  },
})<{
  hasError: boolean;
  isUnit: boolean;
}>(({ hasError, isUnit }) => ({
  fontFamily: "Encode Sans Semi Condensed",
  padding: "10px",
  height: "35px",
  marginBottom: hasError ? "5px" : "14px",
  borderRadius: "0px",
  border: hasError && !isUnit ? "1px solid rgba(214, 40, 57, 1) " : "1px solid #9E9E9E66",
  backgroundColor: hasError ? "rgba(211, 47, 47, 0.02)" : "transparent",
  "& .MuiFormHelperText-root": {
    marginLeft: "0px",
    fontSize: "12px",
    color: ordinaryColors.error,
  },

  " .MuiOutlinedInput-notchedOutline": {
    borderWidth: "0px",
  },
  "& .MuiOutlinedInput-input": {
    padding: "0.5rem 0.75rem",
    width: "100%",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "0px",
    border: hasError && !isUnit ? "1px solid rgba(214, 40, 57, 1)" : "1px solid #9E9E9E66",
    backgroundColor: hasError ? "rgba(211, 47, 47, 0.02)" : "transparent",
    padding: "0px",
    "& fieldset": {
      borderRadius: "0px",
    },
  },
}));

const TextAreaForInstructions: React.FC<Props> = (props) => {
  const {
    title,

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data: { state, setstate, onBlur, updateValue, setNull, setAndSave },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    placeholder = "placeholder",
    disabled,
    testId,
    errors,
    isRequiredField,
    errorManager,
    className,
    isUnit,
    hideLabel,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...rest
  } = props;

  // Watch certificate form for status changes
  const certificateFormAtom = getCertificateCreateFormAtom({});
  const certificateValues = useRecoilValue(certificateFormAtom);
  const currentStatusId = certificateValues?.certificateValidityStatusId;

  // Watch validation requirement atoms
  const isUploadValidExtendedBy = useRecoilValue(isUploadValidExtendedByAtom);
  const isCertificateToReplaceOperation = useRecoilValue(isCertificateToReplaceOperationAtom);

  // Check if instructions are required (same logic as validation rules)
  const isInstructionsRequired = React.useMemo(() => {
    return (
      isUploadValidExtendedBy ||
      currentStatusId === STATUS_IDS.WITHDRAWN_NOT_REPLACED ||
      currentStatusId === STATUS_IDS.VALID_EXTENDED_BY ||
      isCertificateToReplaceOperation
    );
  }, [isUploadValidExtendedBy, currentStatusId, isCertificateToReplaceOperation]);

  // Custom validation that matches the central validation
  const customValidationError = React.useMemo(() => {
    if (isInstructionsRequired && (typeof state !== "string" || state.length < 3)) {
      return translations.globals.validationMessages.required;
    }
    if (state && hasNonLatinCharacters(state)) {
      return translations.pages.certificateUpload.errorMessages.nonLatinCharacters;
    }
    return undefined;
  }, [isInstructionsRequired, state]);

  // Track previous status to detect changes
  const [previousStatusId, setPreviousStatusId] = React.useState<string | undefined>(currentStatusId);

  // Clear instructions field when status changes between the two specific statuses
  useEffect(() => {
    if (previousStatusId !== currentStatusId) {
      const isTargetStatus =
        currentStatusId === STATUS_IDS.WITHDRAWN_NOT_REPLACED || currentStatusId === STATUS_IDS.VALID_EXTENDED_BY;
      const wasPreviouslyTargetStatus =
        previousStatusId === STATUS_IDS.WITHDRAWN_NOT_REPLACED || previousStatusId === STATUS_IDS.VALID_EXTENDED_BY;

      // Clear instructions when:
      // 1. Moving TO one of the target statuses from a different status
      // 2. Moving BETWEEN the two target statuses
      if (isTargetStatus && previousStatusId !== undefined) {
        if (!wasPreviouslyTargetStatus || (wasPreviouslyTargetStatus && previousStatusId !== currentStatusId)) {
          setstate("");
        }
      }

      setPreviousStatusId(currentStatusId);
    }
  }, [currentStatusId, previousStatusId, setstate]);

  const change: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const value = e.target.value;

    if (value === null) {
      if (setNull) {
        setNull();
      } else {
        setstate("");
      }
    } else {
      if (typeof value === "string") setstate(value);
    }
  };

  const handleBlur = () => {
    onBlur();
    // Force validation by triggering a validation check
    // This ensures validation runs when the user leaves the field
  };

  const errorManagerRes = errorManager({
    errorJoiner: (errors) => {
      return errors.join(", ");
    },
  });

  // Combine central validation errors with custom validation
  const hasValidationError = errorManagerRes.error || !!customValidationError;
  const validationHelperText = customValidationError || errorManagerRes.helperText;

  return (
    <>
      <StyledTextArea
        hasError={hasValidationError}
        isUnit={isUnit ?? false}
        minRows={props.minRows}
        maxRows={props.maxRows}
        onBlur={handleBlur}
        value={state || ""}
        onChange={change}
        disabled={props.disabled}
        className={className}
        // showRemoveIcon={!isRequiredField}
        required={isInstructionsRequired}
        helperText={hasValidationError && !isUnit ? validationHelperText : ""}
        {...errorManagerRes}
      />
      {hasValidationError && (
        <span style={{ color: ordinaryColors.error, fontSize: "12px" }}>{validationHelperText}</span>
      )}
    </>
  );
};

export default TextAreaForInstructions;
